import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingCart,
    faCheckCircle,
    faTimes,
    faExchangeAlt,
    faSearch,
    faRefresh,
    faEye,
    faEdit,
    faTrash,
    faFilePdf,
    faPrint,
    faDownload,
    faBoxes,
    faTag,
    faBuilding,
    faDollarSign,
    faCalendarAlt,
    faClock,
    faUser,
    faStore,
    faInfoCircle,
    faHistory,
    faBalanceScale,
    faArrowRight,
    faArrowLeft,
    faCheckDouble,
    faLayerGroup,
    faPalette,
    faRulerCombined,
    faChartLine,
    faPercentage,
    faFilter,
    faSpinner,
    faExclamationTriangle  // ADD THIS LINE - missing import
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import "../styles/CustomerList.css";
import "../styles/Purchases.css";

// Translator for the comparison page
const createComparisonTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_comparison_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Comparison");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_comparison_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        orderComparison: 'Order Comparison',
        placeOrdersVsApproved: 'Place Orders vs Approved Products',
        comparisonReport: 'Comparison Report',
        placeOrderDetails: 'Place Order Details',
        approvedProductDetails: 'Approved Product Details',
        stockChanges: 'Stock Changes',
        approvalInfo: 'Approval Information',
        productName: 'Product',
        colorCode: 'Color Code',
        batchNo: 'Batch No',
        category: 'Category',
        categoryType: 'Type',
        bundleRolls: 'Rolls/Bundle',
        yardsPerRoll: 'Yards/Roll',
        quantity: 'Quantity',
        price: 'Price',
        totalAmount: 'Total Amount',
        totalYards: 'Total Yards',
        unit: 'Unit',
        company: 'Company',
        supplier: 'Supplier',
        orderNumber: 'Order #',
        orderDate: 'Order Date',
        approvalDate: 'Approval Date',
        approvedBy: 'Approved By',
        stockBefore: 'Stock Before',
        stockAfter: 'Stock After',
        quantityAdded: 'Quantity Added',
        mergeAction: 'Merge Action',
        existingBatch: 'Existing Batch',
        inStock: 'In Stock',
        outOfStock: 'Out of Stock',
        lowStock: 'Low Stock',
        mergeActionNew: 'New Product',
        mergeActionMerge: 'Merged with Existing',
        mergeActionUpdate: 'Updated Stock',
        searchPlaceholder: 'Search by order #, product, or supplier...',
        filterByOrder: 'Filter by Order',
        filterByProduct: 'Filter by Product',
        filterBySupplier: 'Filter by Supplier',
        filterByDate: 'Filter by Date',
        startDate: 'Start Date',
        endDate: 'End Date',
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        refresh: 'Refresh',
        viewDetails: 'View Details',
        exportPDF: 'Export to PDF',
        print: 'Print',
        close: 'Close',
        totalOrders: 'Total Orders',
        totalItems: 'Total Items',
        totalQuantity: 'Total Quantity',
        totalValue: 'Total Value',
        totalStockAdded: 'Total Stock Added',
        uniqueProducts: 'Unique Products',
        loading: 'Loading comparison data...',
        noData: 'No comparison data found',
        noOrdersFound: 'No orders found for comparison',
        selectOrder: 'Select an order to view comparison',
        errorLoading: 'Error loading comparison data',
        approved: 'Approved',
        pending: 'Pending',
        cancelled: 'Cancelled',
        all: 'All',
        showing: 'Showing',
        of: 'of',
        items: 'items',
        previous: 'Previous',
        next: 'Next',
        viewOriginalOrder: 'View Original Order',
        viewApprovedProduct: 'View Approved Product',
        stockIncreased: 'Stock Increased',
        stockDecreased: 'Stock Decreased',
        noChange: 'No Change',
        quantityDifference: 'Quantity Difference',
        priceDifference: 'Price Difference',
        yardsDifference: 'Yards Difference',
        approvalStatus: 'Approval Status',
        summary: 'Summary',
        approvedCount: 'Approved Items',
        pendingCount: 'Pending Items',
        totalApprovedQuantity: 'Total Approved Quantity',
        totalRejectedQuantity: 'Total Rejected Quantity',
        shopName: 'Shop',
        shopCode: 'Shop Code',
        currentShop: 'Current Shop',
        switchShop: 'Switch Shop',
        businessName: 'Business Name',
        reportGenerated: 'Report Generated',
        generatedBy: 'Generated by',
        noShopSelected: 'No shop selected. Please select a shop first.',
        selectShopFirst: 'Please select a shop to view comparison data'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const comparisonTranslator = createComparisonTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    if (!toast.show) return null;

    return (
        <div className={`toast-notification ${toast.type}`}>
            <div className="toast-content">
                <div className="toast-message">{toast.message}</div>
                <button className="toast-close" onClick={() => setToast({ show: false })}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    return (
        <div className="modal-overlay-modern" onClick={onClose}>
            <div className="modal-content-modern shop-selector-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header-modern">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="close-btn-modern" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body-modern">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>Loading shops...</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>No shops available</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => {
                                        onSelectShop(shop);
                                        onClose();
                                    }}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>Current</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Summary Cards Component
const SummaryCards = ({ orders, approvedItems, translations, currencySymbol = 'Rs' }) => {
    const totalOrders = orders.length;
    const totalPlaceOrderItems = orders.reduce((sum, order) => sum + (order.items?.length || 0), 0);
    const totalApprovedItems = approvedItems.length;
    const totalQuantityApproved = approvedItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
    const totalValueApproved = approvedItems.reduce((sum, item) => sum + ((parseFloat(item.sell_price) || 0) * (parseFloat(item.quantity) || 0)), 0);
    const uniqueProducts = new Set(approvedItems.map(item => item.product_id).filter(id => id)).size;
    const newProducts = approvedItems.filter(item => item.merge_action === 'new' || !item.merge_action).length;
    const mergedProducts = approvedItems.filter(item => item.merge_action === 'merge').length;

    const cards = [
        {
            title: translations.totalOrders || 'Total Orders',
            value: totalOrders,
            icon: faShoppingCart,
            color: '#3b82f6'
        },
        {
            title: translations.totalItems || 'Total Items',
            value: `${totalApprovedItems} / ${totalPlaceOrderItems}`,
            subtitle: 'Approved / Ordered',
            icon: faBoxes,
            color: '#10b981'
        },
        {
            title: translations.totalQuantity || 'Total Quantity',
            value: totalQuantityApproved.toFixed(2),
            icon: faBalanceScale,
            color: '#f59e0b'
        },
        {
            title: translations.totalValue || 'Total Value',
            value: `${currencySymbol} ${totalValueApproved.toFixed(2)}`,
            icon: faDollarSign,
            color: '#ef4444'
        },
        {
            title: translations.uniqueProducts || 'Unique Products',
            value: uniqueProducts,
            subtitle: `${newProducts} New, ${mergedProducts} Merged`,
            icon: faLayerGroup,
            color: '#8b5cf6'
        }
    ];

    return (
        <div className="summary-cards">
            {cards.map((card, index) => (
                <div key={index} className="summary-card" style={{ borderTopColor: card.color }}>
                    <div className="card-icon" style={{ backgroundColor: `${card.color}20`, color: card.color }}>
                        <FontAwesomeIcon icon={card.icon} />
                    </div>
                    <div className="card-content">
                        <h4>{card.title}</h4>
                        <div className="card-value">{card.value}</div>
                        {card.subtitle && <small>{card.subtitle}</small>}
                    </div>
                </div>
            ))}
        </div>
    );
};

// Comparison Row Component
const ComparisonRow = ({ placeOrderItem, approvedItem, index, translations, currencySymbol = 'Rs' }) => {
    const getMergeActionBadge = (action) => {
        const styles = {
            new: { bg: '#10b98120', color: '#10b981', text: translations.mergeActionNew || 'New Product' },
            merge: { bg: '#f59e0b20', color: '#f59e0b', text: translations.mergeActionMerge || 'Merged with Existing' },
            update: { bg: '#3b82f620', color: '#3b82f6', text: translations.mergeActionUpdate || 'Updated Stock' }
        };
        
        const style = styles[action?.toLowerCase()] || styles.new;
        return (
            <span className="merge-badge" style={{ backgroundColor: style.bg, color: style.color }}>
                {style.text}
            </span>
        );
    };

    const getStockChangeIndicator = (before, after) => {
        if (before === undefined && after === undefined) return null;
        const beforeNum = parseFloat(before || 0);
        const afterNum = parseFloat(after || 0);
        const diff = afterNum - beforeNum;
        if (diff > 0) {
            return (
                <span className="stock-change positive">
                    <FontAwesomeIcon icon={faArrowRight} /> +{diff.toFixed(2)}
                </span>
            );
        } else if (diff < 0) {
            return (
                <span className="stock-change negative">
                    <FontAwesomeIcon icon={faArrowRight} /> {diff.toFixed(2)}
                </span>
            );
        }
        return (
            <span className="stock-change neutral">
                <FontAwesomeIcon icon={faBalanceScale} /> No Change
            </span>
        );
    };

    const placeOrderQty = parseFloat(placeOrderItem?.quantity || 0);
    const approvedQty = parseFloat(approvedItem?.quantity || 0);
    const quantityDiff = approvedQty - placeOrderQty;
    
    const placeOrderPrice = parseFloat(placeOrderItem?.price || 0);
    const approvedPrice = parseFloat(approvedItem?.sell_price || 0);
    const priceDiff = approvedPrice - placeOrderPrice;

    return (
        <tr className="comparison-row">
            <td className="centered-cell">{index + 1}</td>
            
            {/* Place Order Side */}
            <td className="place-order-cell">
                <div className="product-info">
                    <span className="product-name">{placeOrderItem?.product_name || '-'}</span>
                    {placeOrderItem?.product_colorcode && (
                        <span 
                            className="color-badge"
                            style={{ backgroundColor: placeOrderItem.product_colorcode }}
                            title={placeOrderItem.product_colorcode}
                        />
                    )}
                </div>
            </td>
            <td className="centered-cell">{placeOrderItem?.product_colorcode || '-'}</td>
            <td className="centered-cell">{placeOrderItem?.batch_no || '-'}</td>
            <td className="centered-cell">{placeOrderItem?.category || '-'}</td>
            <td className="centered-cell">
                <span className="type-badge">
                    {placeOrderItem?.category_type === 'bundle' ? 'Bundle' : 
                     placeOrderItem?.category_type === 'roll' ? 'Roll' : '-'}
                </span>
            </td>
            <td className="centered-cell">{placeOrderItem?.bundle_rolls || '-'}</td>
            <td className="centered-cell">{parseFloat(placeOrderItem?.yards_per_roll || 0).toFixed(2)}</td>
            <td className="centered-cell">{parseFloat(placeOrderItem?.quantity || 0).toFixed(3)}</td>
            <td className="centered-cell">{currencySymbol} {parseFloat(placeOrderItem?.price || 0).toFixed(2)}</td>
            <td className="centered-cell">{currencySymbol} {parseFloat(placeOrderItem?.total || 0).toFixed(2)}</td>
            <td className="centered-cell">{parseFloat(placeOrderItem?.total_yards || 0).toFixed(2)}</td>
            <td className="centered-cell">{placeOrderItem?.unit || 'yards'}</td>
            
            {/* Approved Side */}
            <td className="approved-cell">
                <div className="product-info">
                    <span className="product-name">{approvedItem?.product_name || placeOrderItem?.product_name || '-'}</span>
                    {approvedItem?.product_colorcode && (
                        <span 
                            className="color-badge"
                            style={{ backgroundColor: approvedItem.product_colorcode }}
                            title={approvedItem.product_colorcode}
                        />
                    )}
                </div>
            </td>
            <td className="centered-cell">{approvedItem?.product_colorcode || '-'}</td>
            <td className="centered-cell">
                {approvedItem?.batch_no || '-'}
                {approvedItem?.existing_batch_no && (
                    <small className="existing-batch"> (from: {approvedItem.existing_batch_no})</small>
                )}
            </td>
            <td className="centered-cell">{approvedItem?.category || '-'}</td>
            <td className="centered-cell">
                <span className="type-badge">
                    {approvedItem?.category_type === 'bundle' ? 'Bundle' : 
                     approvedItem?.category_type === 'roll' ? 'Roll' : '-'}
                </span>
            </td>
            <td className="centered-cell">{approvedItem?.bundle_rolls || '-'}</td>
            <td className="centered-cell">{parseFloat(approvedItem?.yards_per_roll || 0).toFixed(2)}</td>
            <td className="centered-cell">
                {parseFloat(approvedItem?.quantity || 0).toFixed(3)}
            </td>
            <td className="centered-cell">{currencySymbol} {parseFloat(approvedItem?.sell_price || 0).toFixed(2)}</td>
            <td className="centered-cell">{currencySymbol} {parseFloat((approvedItem?.sell_price || 0) * (approvedItem?.quantity || 0)).toFixed(2)}</td>
            <td className="centered-cell">{parseFloat(approvedItem?.total_yards || 0).toFixed(2)}</td>
            <td className="centered-cell">{approvedItem?.unit || 'yards'}</td>
            
            {/* Stock Changes */}
            <td className="centered-cell">
                <div className="stock-info">
                    <div className="stock-before">
                        <small>Before:</small> {parseFloat(approvedItem?.quantity_before || 0).toFixed(2)}
                    </div>
                    <div className="stock-after">
                        <small>After:</small> {parseFloat(approvedItem?.quantity_after || 0).toFixed(2)}
                    </div>
                    <div className="stock-diff">
                        {getStockChangeIndicator(approvedItem?.quantity_before, approvedItem?.quantity_after)}
                    </div>
                </div>
            </td>
            
            {/* Approval Info */}
            <td className="centered-cell">
                {getMergeActionBadge(approvedItem?.merge_action)}
                {approvedItem?.existing_product_id && (
                    <div className="existing-product-id">
                        <small>Product ID: {approvedItem.existing_product_id}</small>
                    </div>
                )}
            </td>
            <td className="centered-cell">
                <div className="approval-details">
                    <div>{approvedItem?.approved_by_username || '-'}</div>
                    <small>{approvedItem?.approved_at ? new Date(approvedItem.approved_at).toLocaleDateString() : '-'}</small>
                </div>
            </td>
            
            {/* Differences */}
            <td className={`centered-cell ${quantityDiff !== 0 ? (quantityDiff > 0 ? 'positive-diff' : 'negative-diff') : ''}`}>
                {quantityDiff !== 0 ? (quantityDiff > 0 ? '+' : '') + quantityDiff.toFixed(3) : '0'}
            </td>
            <td className={`centered-cell ${priceDiff !== 0 ? (priceDiff > 0 ? 'positive-diff' : 'negative-diff') : ''}`}>
                {priceDiff !== 0 ? (priceDiff > 0 ? '+' : '') + priceDiff.toFixed(2) : '0'}
            </td>
        </tr>
    );
};

// Order Selector Component
const OrderSelector = ({ orders, selectedOrderId, onSelectOrder, translations, loading }) => {
    if (loading) {
        return (
            <div className="order-selector">
                <div className="selector-header">
                    <FontAwesomeIcon icon={faShoppingCart} />
                    <span>{translations.filterByOrder || 'Select Order'}</span>
                </div>
                <div className="selector-list loading">
                    <div className="loading-spinner-small"></div>
                    <p>Loading orders...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="order-selector">
            <div className="selector-header">
                <FontAwesomeIcon icon={faShoppingCart} />
                <span>{translations.filterByOrder || 'Select Order'}</span>
            </div>
            <div className="selector-list">
                {orders.length === 0 ? (
                    <div className="no-orders">
                        <p>{translations.noOrdersFound || 'No orders found'}</p>
                    </div>
                ) : (
                    orders.map(order => (
                        <div
                            key={order.id}
                            className={`order-item ${selectedOrderId === order.id ? 'active' : ''}`}
                            onClick={() => onSelectOrder(order.id)}
                        >
                            <div className="order-number">#{order.order_number || order.id}</div>
                            <div className="order-supplier">{order.supplier_name}</div>
                            <div className="order-date">{order.order_date ? new Date(order.order_date).toLocaleDateString() : '-'}</div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

// Main Comparison Component
const OrderComparison = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [orders, setOrders] = useState([]);
    const [approvedItems, setApprovedItems] = useState([]);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [loadingApproved, setLoadingApproved] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredItems, setFilteredItems] = useState([]);
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);
    
    // Filter states
    const [mergeActionFilter, setMergeActionFilter] = useState('');
    
    // Toast state
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    
    // Translations
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [translationsLoaded, setTranslationsLoaded] = useState(false);
    
    // Currency
    const [currentCurrency, setCurrentCurrency] = useState(() => {
        const saved = localStorage.getItem('ourzest_currency');
        return saved ? JSON.parse(saved) : { code: 'PKR', symbol: 'Rs', name: 'Pakistani Rupee' };
    });
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    
    // Business info
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        businessaddress: '',
        fullname: '',
        email: ''
    });

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            try {
                const trans = await comparisonTranslator.getTranslations(currentLanguage);
                setTranslations(trans);
                setTranslationsLoaded(true);
            } catch (error) {
                console.error('Error loading translations:', error);
                setTranslationsLoaded(true);
            }
        };
        loadTranslations();
    }, [currentLanguage]);

    // Apply filters
    useEffect(() => {
        let filtered = [...approvedItems];
        
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(item =>
                item.product_name?.toLowerCase().includes(term) ||
                item.order_number?.toLowerCase().includes(term) ||
                item.supplier_name?.toLowerCase().includes(term) ||
                item.batch_no?.toLowerCase().includes(term)
            );
        }
        
        if (mergeActionFilter) {
            filtered = filtered.filter(item => item.merge_action === mergeActionFilter);
        }
        
        setFilteredItems(filtered);
        setCurrentPage(1);
    }, [approvedItems, searchTerm, mergeActionFilter]);

    // Fetch available shops
    const fetchAvailableShops = async () => {
        if (!user) return;
        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            setAvailableShops([]);
        } finally {
            setLoadingShops(false);
        }
    };

    // Fetch orders
    const fetchOrders = async () => {
        if (!user?.username) {
            console.log('No user found');
            setLoading(false);
            return;
        }
        
        if (!currentShop) {
            console.log('No current shop selected');
            setLoading(false);
            return;
        }
        
        setLoadingOrders(true);
        
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/placeorder.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    filters: {}
                })
            });
            
            const data = await response.json();
            if (data.status === 'success') {
                setOrders(data.data || []);
                // Auto-select the first order if available
                if (data.data && data.data.length > 0 && !selectedOrderId) {
                    setSelectedOrderId(data.data[0].id);
                    setSelectedOrder(data.data[0]);
                }
            } else {
                console.error('Error fetching orders:', data.message);
                setToast({ show: true, type: 'error', message: data.message || 'Error loading orders' });
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            setToast({ show: true, type: 'error', message: translations.errorLoading || 'Error loading orders' });
        } finally {
            setLoadingOrders(false);
            setLoading(false);
        }
    };

    // Fetch approved items for selected order
    const fetchApprovedItems = async (orderId) => {
        if (!orderId || !currentShop) return;
        
        setLoadingApproved(true);
        
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/approved_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_approved_by_order',
                    order_id: orderId,
                    shop_id: currentShop.shop_id
                })
            });
            
            const data = await response.json();
            if (data.status === 'success') {
                setApprovedItems(data.data || []);
                setFilteredItems(data.data || []);
            } else {
                setApprovedItems([]);
                setFilteredItems([]);
            }
        } catch (error) {
            console.error('Error fetching approved items:', error);
            setApprovedItems([]);
            setFilteredItems([]);
        } finally {
            setLoadingApproved(false);
        }
    };

    // Fetch business info
    const fetchBusinessInfo = async () => {
        if (!user?.username) return;
        
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });
            
            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.businessname || '',
                    businessaddress: data.businessaddress || '',
                    fullname: data.fullname || '',
                    email: data.email || ''
                });
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
        }
    };

    // Load data when selected order changes
    useEffect(() => {
        if (selectedOrderId && currentShop) {
            const order = orders.find(o => o.id === selectedOrderId);
            setSelectedOrder(order);
            fetchApprovedItems(selectedOrderId);
        }
    }, [selectedOrderId, orders]);

    // Initial load
    useEffect(() => {
        if (translationsLoaded && user) {
            if (currentShop) {
                fetchOrders();
                fetchBusinessInfo();
            } else {
                setLoading(false);
                fetchAvailableShops();
            }
        }
    }, [translationsLoaded, user, currentShop]);

    // Handle shop switch
    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(
                shop.shop_id,
                shop.shop_name,
                shop.shop_code
            );
            
            if (success) {
                setToast({ show: true, type: 'success', message: 'Shop switched successfully' });
                // Reset states
                setOrders([]);
                setApprovedItems([]);
                setSelectedOrderId(null);
                setSelectedOrder(null);
                // Fetch orders for new shop
                fetchOrders();
            } else {
                setToast({ show: true, type: 'error', message: 'Failed to switch shop' });
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            setToast({ show: true, type: 'error', message: 'Failed to switch shop' });
        }
    };

    // Export to PDF
    const exportToPDF = async () => {
        if (!filteredItems.length && !selectedOrder) return;
        
        setExporting(true);
        
        try {
            const doc = new jsPDF('landscape', 'mm', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            
            // Add header
            doc.setFontSize(18);
            doc.setTextColor(0, 0, 0);
            doc.text(translations.orderComparison || 'Order Comparison', pageWidth / 2, 15, { align: 'center' });
            
            doc.setFontSize(10);
            doc.text(`${businessInfo.businessname || ''}`, pageWidth / 2, 22, { align: 'center' });
            doc.setFontSize(8);
            doc.text(`${translations.reportGenerated || 'Report Generated'}: ${new Date().toLocaleString()}`, pageWidth / 2, 28, { align: 'center' });
            doc.text(`${translations.generatedBy || 'Generated by'}: ${user?.fullname || user?.username}`, pageWidth / 2, 33, { align: 'center' });
            
            // Order info
            if (selectedOrder) {
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 0);
                doc.text(`${translations.orderNumber || 'Order #'}: ${selectedOrder.order_number || selectedOrder.id}`, 10, 40);
                doc.text(`${translations.supplier || 'Supplier'}: ${selectedOrder.supplier_name}`, 10, 46);
                doc.text(`${translations.orderDate || 'Order Date'}: ${selectedOrder.order_date ? new Date(selectedOrder.order_date).toLocaleDateString() : '-'}`, 10, 52);
                doc.text(`${translations.shopName || 'Shop'}: ${currentShop?.shop_name || '-'}`, 10, 58);
            }
            
            // Prepare table data
            const tableData = filteredItems.map((item, index) => [
                index + 1,
                item.product_name || '-',
                item.product_colorcode || '-',
                item.batch_no || '-',
                parseFloat(item.quantity_before || 0).toFixed(2),
                parseFloat(item.quantity || 0).toFixed(2),
                parseFloat(item.quantity_after || 0).toFixed(2),
                item.merge_action || 'new',
                item.approved_by_username || '-',
                item.approved_at ? new Date(item.approved_at).toLocaleDateString() : '-'
            ]);
            
            autoTable(doc, {
                startY: 65,
                head: [[
                    '#',
                    translations.productName || 'Product',
                    translations.colorCode || 'Color',
                    translations.batchNo || 'Batch',
                    translations.stockBefore || 'Before',
                    translations.quantityAdded || 'Added',
                    translations.stockAfter || 'After',
                    translations.mergeAction || 'Action',
                    translations.approvedBy || 'Approved By',
                    translations.approvalDate || 'Date'
                ]],
                body: tableData,
                theme: 'striped',
                headStyles: {
                    fillColor: [16, 185, 129],
                    textColor: [255, 255, 255],
                    fontSize: 8
                },
                bodyStyles: {
                    fontSize: 7
                },
                margin: { left: 10, right: 10 }
            });
            
            doc.save(`order_comparison_${selectedOrder?.order_number || 'report'}.pdf`);
            setToast({ show: true, type: 'success', message: 'PDF exported successfully' });
        } catch (error) {
            console.error('Error generating PDF:', error);
            setToast({ show: true, type: 'error', message: 'Error generating PDF' });
        } finally {
            setExporting(false);
        }
    };

    // Pagination
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);
    
    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // Show loading while translations are loading
    if (!translationsLoaded) {
        return (
            <div className="comparison-container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading translations...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`comparison-container ${isRTL ? 'rtl' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />
            
            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />
            
            {/* Header */}
            <div className="comparison-header">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faExchangeAlt} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.orderComparison || 'Order Comparison'}</h1>
                        <p>{translations.placeOrdersVsApproved || 'Place Orders vs Approved Products'}</p>
                        {currentShop ? (
                            <div className="shop-badge">
                                {/* <FontAwesomeIcon icon={faStore} /> */}
                                {/* <span>{currentShop.shop_name} {currentShop.shop_code && `(${currentShop.shop_code})`}</span> */}
                            </div>
                        ) : (
                            <div className="shop-badge warning">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                <span>{translations.noShopSelected || 'No shop selected'}</span>
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="header-actions">
                    {!currentShop && (
                        <button
                            onClick={() => setShowShopSelector(true)}
                            className="btn-primary"
                        >
                            <FontAwesomeIcon icon={faStore} />
                            <span>{translations.switchShop || 'Select Shop'}</span>
                        </button>
                    )}
                    <button
                        onClick={exportToPDF}
                        className="btn-export"
                        disabled={exporting || filteredItems.length === 0 || !currentShop}
                    >
                        <FontAwesomeIcon icon={faFilePdf} />
                        <span>{exporting ? 'Exporting...' : (translations.exportPDF || 'Export PDF')}</span>
                    </button>
                    <button 
                        onClick={() => {
                            if (currentShop) {
                                fetchOrders();
                            } else {
                                setShowShopSelector(true);
                            }
                        }} 
                        className="btn-refresh"
                        disabled={loadingOrders}
                    >
                        <FontAwesomeIcon icon={faRefresh} spin={loadingOrders} />
                        <span>{translations.refresh || 'Refresh'}</span>
                    </button>
                </div>
            </div>
            
            {/* No Shop Selected Warning */}
            {!currentShop && (
                <div className="no-shop-warning">
                    <div className="warning-content">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShopFirst || 'Please select a shop to view comparison data'}</p>
                        <button className="btn-primary" onClick={() => setShowShopSelector(true)}>
                            <FontAwesomeIcon icon={faStore} />
                            <span>{translations.switchShop || 'Select Shop'}</span>
                        </button>
                    </div>
                </div>
            )}
            
            {/* Main Content - Only show if shop is selected */}
            {currentShop && (
                <>
                    {/* Summary Cards */}
                    {selectedOrder && approvedItems.length > 0 && (
                        <SummaryCards
                            orders={[selectedOrder]}
                            approvedItems={approvedItems}
                            translations={translations}
                            currencySymbol={currentCurrency.symbol}
                        />
                    )}
                    
                    <div className="comparison-main">
                        {/* Order Selector Sidebar */}
                        <div className="comparison-sidebar">
                            <OrderSelector
                                orders={orders}
                                selectedOrderId={selectedOrderId}
                                onSelectOrder={setSelectedOrderId}
                                translations={translations}
                                loading={loadingOrders}
                            />
                        </div>
                        
                        {/* Comparison Table */}
                        <div className="comparison-content">
                            {/* Filters */}
                            {approvedItems.length > 0 && (
                                <div className="filters-bar">
                                    <div className="search-box">
                                        <FontAwesomeIcon icon={faSearch} />
                                        <input
                                            type="text"
                                            placeholder={translations.searchPlaceholder || 'Search by product, order #, or supplier...'}
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    
                                    <select
                                        value={mergeActionFilter}
                                        onChange={(e) => setMergeActionFilter(e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="">{translations.all || 'All Actions'}</option>
                                        <option value="new">{translations.mergeActionNew || 'New Products'}</option>
                                        <option value="merge">{translations.mergeActionMerge || 'Merged Products'}</option>
                                        <option value="update">{translations.mergeActionUpdate || 'Stock Updates'}</option>
                                    </select>
                                </div>
                            )}
                            
                            {/* Loading State */}
                            {loadingOrders ? (
                                <div className="loading-container">
                                    <div className="loading-spinner"></div>
                                    <p>{translations.loading || 'Loading orders...'}</p>
                                </div>
                            ) : loadingApproved && selectedOrderId ? (
                                <div className="loading-container">
                                    <div className="loading-spinner"></div>
                                    <p>{translations.loading || 'Loading approved products...'}</p>
                                </div>
                            ) : orders.length === 0 ? (
                                <div className="no-data-container">
                                    <FontAwesomeIcon icon={faShoppingCart} />
                                    <p>{translations.noOrdersFound || 'No orders found for this shop'}</p>
                                    <button 
                                        className="btn-primary"
                                        onClick={() => window.location.href = '/AddFabPlaceOrder'}
                                    >
                                        <FontAwesomeIcon icon={faShoppingCart} />
                                        <span>Create First Order</span>
                                    </button>
                                </div>
                            ) : !selectedOrder ? (
                                <div className="no-data-container">
                                    <FontAwesomeIcon icon={faShoppingCart} />
                                    <p>{translations.selectOrder || 'Select an order from the sidebar to view comparison'}</p>
                                </div>
                            ) : currentItems.length === 0 && !loadingApproved ? (
                                <div className="no-data-container">
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                    <p>{translations.noData || 'No approved products found for this order'}</p>
                                    {selectedOrder && selectedOrder.approve_status !== 'Approved' && (
                                        <div className="info-message">
                                            <FontAwesomeIcon icon={faInfoCircle} />
                                            <p>This order has not been approved yet. Once approved, products will appear here.</p>
                                        </div>
                                    )}
                                </div>
                            ) : currentItems.length > 0 ? (
                                <>
                                    {/* Comparison Table */}
                                    <div className="table-container">
                                        <table className="comparison-table">
                                            <thead>
                                                <tr>
                                                    <th rowSpan="2" className="centered-cell">#</th>
                                                    <th colSpan="12" className="place-order-header">Place Order Details</th>
                                                    <th colSpan="12" className="approved-header">Approved Product Details</th>
                                                    <th rowSpan="2" className="centered-cell">Stock Changes</th>
                                                    <th rowSpan="2" className="centered-cell">Approval Info</th>
                                                    <th rowSpan="2" className="centered-cell">Qty Diff</th>
                                                    <th rowSpan="2" className="centered-cell">Price Diff</th>
                                                </tr>
                                                <tr>
                                                    <th>{translations.productName || 'Product'}</th>
                                                    <th>{translations.colorCode || 'Color'}</th>
                                                    <th>{translations.batchNo || 'Batch'}</th>
                                                    <th>{translations.category || 'Category'}</th>
                                                    <th>{translations.categoryType || 'Type'}</th>
                                                    <th>{translations.bundleRolls || 'R/Bundle'}</th>
                                                    <th>{translations.yardsPerRoll || 'Yds/Roll'}</th>
                                                    <th>{translations.quantity || 'Qty'}</th>
                                                    <th>{translations.price || 'Price'}</th>
                                                    <th>{translations.totalAmount || 'Total'}</th>
                                                    <th>{translations.totalYards || 'Yards'}</th>
                                                    <th>{translations.unit || 'Unit'}</th>
                                                    <th>{translations.productName || 'Product'}</th>
                                                    <th>{translations.colorCode || 'Color'}</th>
                                                    <th>{translations.batchNo || 'Batch'}</th>
                                                    <th>{translations.category || 'Category'}</th>
                                                    <th>{translations.categoryType || 'Type'}</th>
                                                    <th>{translations.bundleRolls || 'R/Bundle'}</th>
                                                    <th>{translations.yardsPerRoll || 'Yds/Roll'}</th>
                                                    <th>{translations.quantity || 'Qty'}</th>
                                                    <th>{translations.price || 'Price'}</th>
                                                    <th>{translations.totalAmount || 'Total'}</th>
                                                    <th>{translations.totalYards || 'Yards'}</th>
                                                    <th>{translations.unit || 'Unit'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentItems.map((item, index) => {
                                                    const placeOrderItem = selectedOrder?.items?.find(
                                                        poItem => poItem.product_id === item.product_id || 
                                                                  (poItem.product_name === item.product_name && 
                                                                   poItem.product_colorcode === item.product_colorcode)
                                                    );
                                                    return (
                                                        <ComparisonRow
                                                            key={item.id || index}
                                                            placeOrderItem={placeOrderItem}
                                                            approvedItem={item}
                                                            index={startIndex + index}
                                                            translations={translations}
                                                            currencySymbol={currentCurrency.symbol}
                                                        />
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    
                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <div className="pagination">
                                            <button
                                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                disabled={currentPage === 1}
                                                className="pagination-btn"
                                            >
                                                <FontAwesomeIcon icon={faArrowLeft} />
                                                {translations.previous || 'Previous'}
                                            </button>
                                            <span className="page-info">
                                                {translations.showing || 'Showing'} {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredItems.length)} {translations.of || 'of'} {filteredItems.length} {translations.items || 'items'}
                                            </span>
                                            <button
                                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                disabled={currentPage === totalPages}
                                                className="pagination-btn"
                                            >
                                                {translations.next || 'Next'}
                                                <FontAwesomeIcon icon={faArrowRight} />
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : null}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default OrderComparison;